import {
    AggregateReportProperties, Periods, proFormaReturnsCashFlowDataKeys, proFormaReturnsDataKeys, PropertyKeyToLabelMap, spacerRows,
} from 'components/ProFormaReturnsTable/constant';
import { format, parseISO } from 'date-fns';
import { IProFormaReturnsData } from 'views/ProFormaTable/types';
import { IAggregateReportResponse, IPartnerReturns, IPartnerReturnsByProForma } from 'views/Reports/types';

const EXCEL_NSBLUE = 'e6e8e8';
const EXCEL_WHITE = '0FB3FF';
const BORDER_COLOR = '000000';
const LIGHT_GRAY = 'F2F2F2';
const EXCEL_DARK_GRAY = 'BFBFBF';

const excelDecimalFormat = '#,##0.00';
const excelPercentFormat = '0.00%';
const excelBasisPointFormat = '#,##0" bps"';
const excelMultiplierFormat = '#,##0.00x';
const excelBracesFormat = '"("#,##0.00")"';
const defaultFontSize = 12;
const borderStyle = { style: 'medium', color: { argb: BORDER_COLOR } };
const thinBorderStyle = { style: 'thin', color: { argb: BORDER_COLOR } };

const proFormaReturnsTable = (proFormaReturns: IProFormaReturnsData, periodColumnHeaders: string[], cadence: string) => {
    const grayRows = [
        AggregateReportProperties.TOTAL_DEVELOPMENT_COST,
        AggregateReportProperties.TOTAL_INTEREST,
        AggregateReportProperties.TOTAL_LOAN_DRAWS_DURING_DEVELOPMENT,
        AggregateReportProperties.CASH_FLOW_FROM_OPERATIONS,
        AggregateReportProperties.NET_SALE_PROCEEDS,
        AggregateReportProperties.EQUITY_PROCEEDS_FROM_CAPITAL_EVENTS,
    ];
    const borderedCell = [...spacerRows, AggregateReportProperties.PERMANENT_LOAN_PAYOFF];
    const bracesCell = [
        AggregateReportProperties.PERMANENT_LOAN_CLOSING_COSTS,
        AggregateReportProperties.CONSTRUCTION_LOAN_PAYOFF,
        AggregateReportProperties.PERMANENT_LOAN_PAYOFF,
    ];

    const cashFlow = cadence === Periods.MONTHLY
        ? proFormaReturns.cashFlow
        : proFormaReturns.yearlyCashFlow;

    const proFormaReturnsTableTitleRow = {
        rowFont: {
            bold: true,
            size: defaultFontSize,
        },
        rowCells: [
            {
                value: 'Property Returns',
            },
        ],
    };

    const tableHeaderLabelsRow = {
        rowFont: {
            bold: true,
            size: defaultFontSize,
            color: { argb: EXCEL_NSBLUE },
        },
        rowFill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: EXCEL_WHITE },
        },
        rowCells: [
            {
                value: 'Property',
                width: 30,
                border: {
                    top: borderStyle,
                },
            },
            {
                value: '',
                width: 30,
                border: {
                    top: borderStyle,
                },
            },
            {
                value: '',
                width: 30,
                border: {
                    top: borderStyle,
                },
            },
            {
                value: 'Amount',
                width: 30,
                border: {
                    top: borderStyle,
                },
                alignment: { horizontal: 'right' },
            },
            {
                value: '',
                width: 1,
                border: {
                    left: thinBorderStyle,
                    right: thinBorderStyle,
                },
                cellFill: {
                    type: 'pattern',
                },
            },
            ...periodColumnHeaders.map((col, idx) => {
                const dateFormat = cadence === Periods.ANNUALLY ? 'yyyy' : 'MMM yyyy';
                return {
                    value: format(parseISO(col), dateFormat).toUpperCase(),
                    border: {
                        top: borderStyle,
                        right: idx === periodColumnHeaders.length - 1 ? thinBorderStyle : undefined,
                    },
                    alignment: { horizontal: 'right' },
                };
            }),
        ],
    };

    const proFormaReturnsTableData = proFormaReturnsDataKeys.map((prop, rowIdx) => {
        const propLabel = PropertyKeyToLabelMap[prop as keyof typeof PropertyKeyToLabelMap];
        const isSpacerRow = borderedCell.includes(propLabel);

        const grayRowFill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: propLabel === AggregateReportProperties.EQUITY_PROCEEDS_FROM_CAPITAL_EVENTS ? EXCEL_DARK_GRAY : LIGHT_GRAY },
        };

        let bottomRowStyle: typeof borderStyle | typeof thinBorderStyle | undefined;
        if (rowIdx === proFormaReturnsDataKeys.length - 1) {
            bottomRowStyle = borderStyle;
        } else if (isSpacerRow) {
            bottomRowStyle = thinBorderStyle;
        }

        return {
            rowFill: grayRows.includes(propLabel) ? grayRowFill : undefined,
            rowCells: [
                {
                    value: propLabel,
                    border: {
                        left: borderStyle,
                        bottom: bottomRowStyle,
                    },
                },
                {
                    value: '',
                    border: {
                        bottom: bottomRowStyle,
                    },
                },
                {
                    value: '',
                    border: {
                        bottom: bottomRowStyle,
                    },
                },
                {
                    value: proFormaReturns[prop] ?? 0,
                    border: {
                        bottom: bottomRowStyle,
                    },
                    numFmt: bracesCell.includes(propLabel) ? excelBracesFormat : excelDecimalFormat,
                },
                {
                    value: '',
                    width: 1,
                    border: {
                        left: thinBorderStyle,
                        right: thinBorderStyle,
                    },
                    cellFill: {
                        type: 'pattern',
                    },
                },
                ...cashFlow.map((cf, idx) => ({
                    value: cf[proFormaReturnsCashFlowDataKeys[rowIdx]] || 0,
                    width: 25,
                    border: {
                        right: idx === periodColumnHeaders.length - 1 ? borderStyle : undefined,
                        bottom: bottomRowStyle,
                    },
                    numFmt: bracesCell.includes(propLabel) ? excelBracesFormat : excelDecimalFormat,
                })),
            ],
        };
    });

    return {
        tableTitleRows: [proFormaReturnsTableTitleRow],
        tableHeaderRows: [tableHeaderLabelsRow],
        tableData: proFormaReturnsTableData,
    };
};

const cashFlowTable = (proFormaReturns: IProFormaReturnsData, periodColumnHeaders: string[], cadence: string) => {
    const cashFlow = cadence === Periods.MONTHLY
        ? proFormaReturns.cashFlow
        : proFormaReturns.yearlyCashFlow;

    const cashFlowTableHeaderLabelsRow = {
        rowFont: {
            bold: true,
            size: defaultFontSize,
            color: { argb: EXCEL_NSBLUE },
        },
        rowFill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: EXCEL_WHITE },
        },
        rowCells: [
            {
                value: '',
                border: {
                    top: borderStyle,
                },
                alignment: { horizontal: 'right' },
            },
            {
                value: 'Equity multiple',
                border: {
                    top: borderStyle,
                },
                alignment: { horizontal: 'right' },
            },
            {
                value: 'IRR',
                border: {
                    top: borderStyle,
                },
                alignment: { horizontal: 'right' },
            },
            {
                value: 'Net profit',
                border: {
                    top: borderStyle,
                },
                alignment: { horizontal: 'right' },
            },
            {
                value: '',
                width: 1,
                border: {
                    left: thinBorderStyle,
                    right: thinBorderStyle,
                },
                cellFill: {
                    type: 'pattern',
                },
            },
            ...periodColumnHeaders.map((col, idx) => {
                const dateFormat = cadence === Periods.ANNUALLY ? 'yyyy' : 'MMM yyyy';
                return {
                    value: format(parseISO(col), dateFormat).toUpperCase(),
                    border: {
                        top: borderStyle,
                        right: idx === periodColumnHeaders.length - 1 ? thinBorderStyle : undefined,
                    },
                    alignment: { horizontal: 'right' },
                };
            }),
        ],
    };

    const cashFlowTableData = [{
        rowCells: [
            {
                value: AggregateReportProperties.NET_UNLEVERED_CASH_FLOW,
                border: {
                    left: borderStyle,
                },
            },
            {
                value: proFormaReturns.netUnleveredEquityMultiple,
                numFmt: excelMultiplierFormat,
            },
            {
                value: proFormaReturns.netUnleveredIrr,
                numFmt: excelPercentFormat,
            },
            {
                value: proFormaReturns.netUnleveredNetProfit,
                numFmt: excelDecimalFormat,
            },
            {
                value: '',
                width: 1,
                border: {
                    left: thinBorderStyle,
                    right: thinBorderStyle,
                },
                cellFill: {
                    type: 'pattern',
                },
            },
            ...cashFlow.map((cf, idx) => ({
                value: cf.netUnleveredCashFlow || 0,
                width: 17,
                border: {
                    right: idx === cashFlow.length - 1 ? borderStyle : undefined,
                },
                numFmt: excelDecimalFormat,
            })),
        ],
    },
    {
        rowCells: [
            {
                value: AggregateReportProperties.NET_LEVERED_CASH_FLOW,
                border: {
                    left: borderStyle,
                    bottom: borderStyle,
                },
            },
            {
                value: proFormaReturns.netLeveredEquityMultiple,
                border: {
                    bottom: borderStyle,
                },
                numFmt: excelMultiplierFormat,
            },
            {
                value: proFormaReturns.netLeveredIrr,
                numFmt: excelPercentFormat,
                border: {
                    bottom: borderStyle,
                },
            },
            {
                value: proFormaReturns.netLeveredNetProfit,
                numFmt: excelDecimalFormat,
                border: {
                    bottom: borderStyle,
                },
            },
            {
                value: '',
                width: 1,
                border: {
                    left: thinBorderStyle,
                    right: thinBorderStyle,
                },
                cellFill: {
                    type: 'pattern',
                },
            },
            ...cashFlow.map((cf, idx) => ({
                value: cf.netUnleveredCashFlow || 0,
                width: 17,
                border: {
                    right: idx === periodColumnHeaders.length - 1 ? borderStyle : undefined,
                    bottom: borderStyle,
                },
                numFmt: excelDecimalFormat,
            })),
        ],
    },
    ];

    return {
        tableHeaderRows: [cashFlowTableHeaderLabelsRow],
        tableData: cashFlowTableData,
    };
};

const costMetricsTable = (proFormaReturns: IProFormaReturnsData, periodColumnHeaders: string[]) => {
    const costMetricsTableHeaderLabelsRow = {
        rowFont: {
            bold: true,
            size: defaultFontSize,
            color: { argb: EXCEL_NSBLUE },
        },
        rowFill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: EXCEL_WHITE },
        },
        rowCells: [
            {
                value: '',
                border: {
                    top: borderStyle,
                    left: borderStyle,
                },
                alignment: { horizontal: 'right' },
            },
            {
                value: 'Untrended',
                border: {
                    top: borderStyle,
                },
                alignment: { horizontal: 'right' },
            },
            {
                value: 'Trended',
                border: {
                    top: borderStyle,
                },
                alignment: { horizontal: 'right' },
            },
            {
                value: 'Sale',
                border: {
                    top: borderStyle,
                    right: borderStyle,
                },
                alignment: { horizontal: 'right' },
            },
        ],
    };

    const costMetricsTableData = [{
        rowCells: [
            {
                value: AggregateReportProperties.YIELD_ON_COST,
                border: {
                    left: borderStyle,
                },
            },
            {
                value: proFormaReturns.yieldOnCost.untrended,
                numFmt: excelPercentFormat,
            },
            {
                value: proFormaReturns.yieldOnCost.trended,
                numFmt: excelPercentFormat,
            },
            {
                value: proFormaReturns.yieldOnCost.sale,
                numFmt: excelPercentFormat,
                border: {
                    right: borderStyle,
                },
            },
        ],
    },
    {
        rowCells: [
            {
                value: AggregateReportProperties.MARKET_CAPITALIZATION_RATE,
                border: {
                    left: borderStyle,
                },
            },
            {
                value: proFormaReturns.marketCapitalizationRate.current,
                numFmt: excelPercentFormat,
            },
            {
                value: proFormaReturns.marketCapitalizationRate.trended,
                numFmt: excelPercentFormat,
            },
            {
                value: proFormaReturns.marketCapitalizationRate.onSale,
                numFmt: excelPercentFormat,
                border: {
                    right: borderStyle,
                },
            },
        ],
    },
    {
        rowCells: [
            {
                value: AggregateReportProperties.DEVELOPMENT_SPREAD,
                border: {
                    left: borderStyle,
                    bottom: borderStyle,
                },
            },
            {
                value: proFormaReturns.developmentSpread.untrended,
                numFmt: excelBasisPointFormat,
                border: {
                    bottom: borderStyle,
                },
            },
            {
                value: proFormaReturns.developmentSpread.trended,
                numFmt: excelBasisPointFormat,
                border: {
                    bottom: borderStyle,
                },
            },
            {
                value: proFormaReturns.developmentSpread.sale,
                numFmt: excelBasisPointFormat,
                border: {
                    right: borderStyle,
                    bottom: borderStyle,
                },
            },
        ],
    }];

    return {
        tableHeaderRows: [costMetricsTableHeaderLabelsRow],
        tableData: costMetricsTableData,
    };
};

const partnerReturnsTable = (partnerReturns: IPartnerReturns) => {
    const partnerReturnsTableTitleRow = {
        rowFont: {
            bold: true,
            size: defaultFontSize,
        },
        rowCells: [
            {
                value: 'Partner Returns',
            },
        ],
    };

    const partnerReturnsTableHeaderLabelsRow = {
        rowFont: {
            bold: true,
            size: defaultFontSize,
            color: { argb: EXCEL_NSBLUE },
        },
        rowFill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: EXCEL_WHITE },
        },
        rowCells: [
            {
                value: 'Summary',
                border: {
                    top: borderStyle,
                },
                alignment: { horizontal: 'left' },
            },
            {
                value: 'Contributions',
                border: {
                    top: borderStyle,
                },
                alignment: { horizontal: 'right' },
            },
            {
                value: 'Net profit',
                border: {
                    top: borderStyle,
                },
                alignment: { horizontal: 'right' },
            },
            {
                value: 'ROI',
                border: {
                    top: borderStyle,
                },
                alignment: { horizontal: 'right' },
            },
            {
                value: '',
                border: {
                    top: borderStyle,
                },
            },
            {
                value: 'EMx',
                border: {
                    top: borderStyle,
                },
                alignment: { horizontal: 'right' },
            },
            {
                value: 'IRR',
                border: {
                    top: borderStyle,
                    right: borderStyle,
                },
                alignment: { horizontal: 'right' },
            },
        ],
    };

    const partnerReturnsTableData = [
        {
            rowFill: {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: LIGHT_GRAY },
            },
            rowCells: [
                {
                    value: 'General Partner',
                    border: {
                        left: borderStyle,
                    },
                },
                {
                    value: partnerReturns.gpTotalContribution,
                    numFmt: excelDecimalFormat,
                },
                {
                    value: partnerReturns.gpTotalNetProfit,
                    numFmt: excelDecimalFormat,
                },
                {
                    value: partnerReturns.gpRoi,
                    numFmt: excelPercentFormat,
                },
                {
                    value: '',
                },
                {
                    value: partnerReturns.gpEmx,
                    numFmt: excelMultiplierFormat,
                },
                {
                    value: partnerReturns.gpIrr,
                    numFmt: excelPercentFormat,
                    border: {
                        right: borderStyle,
                    },
                },
            ],
        },
        ...partnerReturns.partnerReturnsByProforma.flatMap((returnsByProForma: IPartnerReturnsByProForma, rowIdx) => {
            const rows = [];

            rows.push({
                rowCells: [
                    {
                        value: `  ${returnsByProForma.dealName} ${returnsByProForma.gp1Name}`,
                        border: {
                            left: borderStyle,
                        },
                    },
                    {
                        value: returnsByProForma.gp1Contributions,
                        numFmt: excelDecimalFormat,
                    },
                    {
                        value: returnsByProForma.gp1NetProfit,
                        numFmt: excelDecimalFormat,
                    },
                    {
                        value: returnsByProForma.gp1Roi,
                        numFmt: excelPercentFormat,
                    },
                    {
                        value: '',
                    },
                    {
                        value: returnsByProForma.gp1Emx,
                        numFmt: excelMultiplierFormat,
                    },
                    {
                        value: returnsByProForma.gp1Irr,
                        numFmt: excelPercentFormat,
                        border: {
                            right: borderStyle,
                        },
                    },
                ],
            });

            if (returnsByProForma.gp2Name) {
                rows.push({
                    rowCells: [
                        {
                            value: `  ${returnsByProForma.dealName} ${returnsByProForma.gp2Name}`,
                            border: {
                                left: borderStyle,
                            },
                        },
                        {
                            value: returnsByProForma.gp2Contributions,
                            numFmt: excelDecimalFormat,
                        },
                        {
                            value: returnsByProForma.gp2NetProfit,
                            numFmt: excelDecimalFormat,
                        },
                        {
                            value: returnsByProForma.gp2Roi,
                            numFmt: excelPercentFormat,
                        },
                        {
                            value: '',
                        },
                        {
                            value: returnsByProForma.gp2Emx,
                            numFmt: excelMultiplierFormat,
                        },
                        {
                            value: returnsByProForma.gp2Irr,
                            numFmt: excelPercentFormat,
                            border: {
                                right: borderStyle,
                            },
                        },
                    ],
                });
            }
            return rows;
        }),
        {
            rowFill: {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: LIGHT_GRAY },
            },
            rowCells: [
                {
                    value: 'Limited Partner',
                    border: {
                        left: borderStyle,
                    },
                },
                {
                    value: partnerReturns.lpTotalContribution,
                    numFmt: excelDecimalFormat,
                },
                {
                    value: partnerReturns.lpTotalNetProfit,
                    numFmt: excelDecimalFormat,
                },
                {
                    value: partnerReturns.lpRoi,
                    numFmt: excelPercentFormat,
                },
                {
                    value: '',
                },
                {
                    value: partnerReturns.lpEmx,
                    numFmt: excelMultiplierFormat,
                },
                {
                    value: partnerReturns.lpIrr,
                    numFmt: excelPercentFormat,
                    border: {
                        right: borderStyle,
                    },
                },
            ],
        },
        ...partnerReturns.partnerReturnsByProforma.map((returnsByProForma, pfIdx) => ({
            rowCells: [
                {
                    value: `  ${returnsByProForma.dealName} - ${returnsByProForma.lpName}`,
                    border: {
                        left: borderStyle,
                        bottom: pfIdx === partnerReturns.partnerReturnsByProforma.length - 1 ? borderStyle : undefined,
                    },
                },
                {
                    value: returnsByProForma.lpContributions,
                    numFmt: excelDecimalFormat,
                    border: {
                        bottom: pfIdx === partnerReturns.partnerReturnsByProforma.length - 1 ? borderStyle : undefined,
                    },
                },
                {
                    value: returnsByProForma.lpNetProfit,
                    numFmt: excelDecimalFormat,
                    border: {
                        bottom: pfIdx === partnerReturns.partnerReturnsByProforma.length - 1 ? borderStyle : undefined,
                    },
                },
                {
                    value: returnsByProForma.lpRoi,
                    numFmt: excelPercentFormat,
                    border: {
                        bottom: pfIdx === partnerReturns.partnerReturnsByProforma.length - 1 ? borderStyle : undefined,
                    },
                },
                {
                    value: '',
                    border: {
                        bottom: pfIdx === partnerReturns.partnerReturnsByProforma.length - 1 ? borderStyle : undefined,
                    },
                },
                {
                    value: returnsByProForma.lpEmx,
                    numFmt: excelMultiplierFormat,
                    border: {
                        bottom: pfIdx === partnerReturns.partnerReturnsByProforma.length - 1 ? borderStyle : undefined,
                    },
                },
                {
                    value: returnsByProForma.lpIrr,
                    numFmt: excelPercentFormat,
                    border: {
                        right: borderStyle,
                        bottom: pfIdx === partnerReturns.partnerReturnsByProforma.length - 1 ? borderStyle : undefined,
                    },
                },
            ],
        })),
    ];

    return {
        tableTitleRows: [partnerReturnsTableTitleRow],
        tableHeaderRows: [partnerReturnsTableHeaderLabelsRow],
        tableData: partnerReturnsTableData,
    };
};

export const formatExcelExportData = (
    title: string,
    subtitle: string,
    data: IAggregateReportResponse,
    cadence: string,
    isLandDevelopment: boolean,
) => {
    const {
        proFormaReturns, partnerReturns, actualsProFormaNames,
    } = data;

    const cashFlow = cadence === Periods.MONTHLY
        ? proFormaReturns.cashFlow
        : proFormaReturns.yearlyCashFlow;

    const periodColumnHeaders = cashFlow.map(item => item.analysisDate);

    const topHeaderRows = [
        {
            rowFont: { bold: true, size: defaultFontSize },
            rowCells: [{ value: title }],
        },
        {
            rowFont: { bold: false, size: defaultFontSize },
            rowCells: [{ value: subtitle }],
        },
    ];

    if (actualsProFormaNames && actualsProFormaNames.length > 0) {
        topHeaderRows.push({
            rowFont: { bold: false, size: defaultFontSize },
            rowCells: [{ value: `Reporting project actuals for ${actualsProFormaNames.join(', ')}` }],
        });
    }

    const hasCostMetrics = !!proFormaReturns.yieldOnCost
        && !!proFormaReturns.marketCapitalizationRate
        && !!proFormaReturns.developmentSpread;
    const showCostMetrics = !isLandDevelopment && hasCostMetrics;

    return {
        topHeaderRows,
        reportTables: [
            proFormaReturnsTable(proFormaReturns, periodColumnHeaders, cadence),
            cashFlowTable(proFormaReturns, periodColumnHeaders, cadence),
            ...(showCostMetrics ? [costMetricsTable(proFormaReturns, periodColumnHeaders)] : []),
            partnerReturnsTable(partnerReturns),
        ],
    };
};

export const transfortmPartnerReturnsData = (partnerReturns: IPartnerReturns) => {
    const gpRow = {
        totals: {
            contributions: partnerReturns.gpTotalContribution,
            netProfit: partnerReturns.gpTotalNetProfit,
            roi: partnerReturns.gpRoi,
            emx: partnerReturns.gpEmx,
            irr: partnerReturns.gpIrr,
        },
        byDeal: partnerReturns.partnerReturnsByProforma.flatMap((partnerReturn: IPartnerReturnsByProForma) => {
            const rows = [];
            if (partnerReturn.gp1Name) {
                rows.push({
                    partnerName: partnerReturn.gp1Name,
                    dealName: partnerReturn.dealName,
                    contributions: partnerReturn.gp1Contributions,
                    netProfit: partnerReturn.gp1NetProfit,
                    roi: partnerReturn.gp1Roi,
                    emx: partnerReturn.gp1Emx,
                    irr: partnerReturn.gp1Irr,
                });
            }

            if (partnerReturn.gp2Name) {
                rows.push({
                    partnerName: partnerReturn.gp2Name,
                    dealName: partnerReturn.dealName,
                    contributions: partnerReturn.gp2Contributions,
                    netProfit: partnerReturn.gp2NetProfit,
                    roi: partnerReturn.gp2Roi,
                    emx: partnerReturn.gp2Emx,
                    irr: partnerReturn.gp2Irr,
                });
            }
            return rows;
        }),
    };

    const lpRow = {
        totals: {
            contributions: partnerReturns.lpTotalContribution,
            netProfit: partnerReturns.lpTotalNetProfit,
            roi: partnerReturns.lpRoi,
            emx: partnerReturns.lpEmx,
            irr: partnerReturns.lpIrr,
        },
        byDeal: partnerReturns.partnerReturnsByProforma.map((partnerReturn: IPartnerReturnsByProForma) => (
            {
                partnerName: partnerReturn.lpName,
                dealName: partnerReturn.dealName,
                contributions: partnerReturn.lpContributions,
                netProfit: partnerReturn.lpNetProfit,
                roi: partnerReturn.lpRoi,
                emx: partnerReturn.lpEmx,
                irr: partnerReturn.lpIrr,
            })),
    };

    return {
        gp: gpRow,
        lp: lpRow,
    };
};
